import { useQuery } from '@tanstack/react-query';
import { api, type Problem } from '../api';
import { FieldGroup, RadioGroup, SelectField, TextField } from '../components/form';
import { Switch } from '../components/ui';
import type { Draft } from './draft';

const LOCATIONS = [
  { value: 'ONSITE' as const, label: 'Onsite' },
  { value: 'OFFICE' as const, label: 'Office' },
  { value: 'OTHER' as const, label: 'Other' },
];

const EMPLOYMENT_TYPES = [
  { value: 'FIXED_TERM' as const, label: 'Fixed term (temporary)' },
  { value: 'PERMANENT' as const, label: 'Permanent' },
];

const YES_NO = [
  { value: 'yes' as const, label: 'Yes' },
  { value: 'no' as const, label: 'No' },
];

export function StepEmployee({
  draft,
  onChange,
  problems,
}: {
  draft: Draft;
  onChange: (next: Draft) => void;
  problems: Problem[];
}) {
  const departments = useQuery({ queryKey: ['departments'], queryFn: () => api.departments() });

  const set = <K extends keyof Draft['employee']>(key: K, value: Draft['employee'][K]) =>
    onChange({ ...draft, employee: { ...draft.employee, [key]: value } });

  // Problems only surface once something has been entered, so a fresh form is
  // not a wall of red before anyone has typed.
  const started = draft.employee.fullName.trim().length > 0;
  const problemFor = (field: string) =>
    started ? problems.find((problem) => problem.field === field)?.message : undefined;

  return (
    <div className="space-y-8">
      <FieldGroup
        title="Employment type"
        description="Before anything else: fixed term or permanent changes what IT needs to know."
      >
        <RadioGroup
          label="Fixed term or permanent?"
          required
          value={draft.employee.employmentType || null}
          onChange={(value) => set('employmentType', value)}
          options={EMPLOYMENT_TYPES}
          error={problemFor('employmentType')}
        />

        {draft.employee.employmentType === 'FIXED_TERM' && (
          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <TextField
              label="Contract end date"
              type="date"
              required
              value={draft.employee.contractEndDate}
              onChange={(value) => set('contractEndDate', value)}
              error={problemFor('contractEndDate')}
              hint="When access should end, unless the contract is extended first."
            />

            <div className="sm:col-span-2">
              <RadioGroup
                label="Are they taking over an existing account?"
                required
                value={
                  draft.employee.takingOverExistingAccount === null
                    ? null
                    : draft.employee.takingOverExistingAccount
                      ? 'yes'
                      : 'no'
                }
                onChange={(value) => set('takingOverExistingAccount', value === 'yes')}
                options={YES_NO}
                error={problemFor('takingOverExistingAccount')}
                hint="Some fixed-term roles step into an account that already exists rather than a new one."
              />
            </div>

            {draft.employee.takingOverExistingAccount && (
              <div className="sm:col-span-2">
                <TextField
                  label="Whose account, and anything IT needs to hand it over"
                  required
                  value={draft.employee.takeoverAccountDetails}
                  onChange={(value) => set('takeoverAccountDetails', value)}
                  error={problemFor('takeoverAccountDetails')}
                  placeholder="e.g. Jane Doe's old account, jane.doe@nhcopper.com"
                  maxLength={500}
                />
              </div>
            )}
          </div>
        )}
      </FieldGroup>

      <FieldGroup
        title="Employee details"
        description="Who is joining, and when. This is section 1 of the paper form."
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <TextField
              label="Full name"
              required
              value={draft.employee.fullName}
              onChange={(value) => set('fullName', value)}
              error={problemFor('fullName')}
              placeholder="As it should appear on their account"
              maxLength={160}
            />
          </div>

          <TextField
            label="ID / passport number"
            hint="Stored encrypted. Only shown to people entitled to this request."
            value={draft.employee.idPassport}
            onChange={(value) => set('idPassport', value)}
            maxLength={40}
          />

          <TextField
            label="Contact number"
            type="tel"
            value={draft.employee.contactNumber}
            onChange={(value) => set('contactNumber', value)}
            maxLength={40}
          />

          <TextField
            label="Job title"
            value={draft.employee.jobTitle}
            onChange={(value) => set('jobTitle', value)}
            maxLength={120}
          />

          <SelectField
            label="Department"
            required
            value={draft.employee.departmentId}
            onChange={(value) => set('departmentId', value)}
            error={problemFor('departmentId')}
            options={(departments.data?.departments ?? []).map((department) => ({
              value: department.id,
              label: department.name,
            }))}
          />

          <TextField
            label="Manager / supervisor"
            value={draft.employee.managerName}
            onChange={(value) => set('managerName', value)}
            maxLength={160}
          />

          <TextField
            label="Start date"
            type="date"
            required
            value={draft.employee.startDate}
            onChange={(value) => set('startDate', value)}
            error={problemFor('startDate')}
            hint="IT works back from this."
          />
        </div>
      </FieldGroup>

      <FieldGroup title="Location">
        <RadioGroup
          label="Where will they be working?"
          required
          value={draft.employee.locationType || null}
          onChange={(value) => set('locationType', value)}
          options={LOCATIONS}
          error={problemFor('locationType')}
        />

        {draft.employee.locationType === 'OTHER' && (
          <div className="mt-4 max-w-md">
            <TextField
              label="Specify the location"
              required
              value={draft.employee.locationOther}
              onChange={(value) => set('locationOther', value)}
              error={problemFor('locationOther')}
              maxLength={160}
            />
          </div>
        )}
      </FieldGroup>

      <FieldGroup title="Email">
        <div className="flex items-start gap-3">
          <Switch
            id="email-required"
            checked={draft.employee.emailRequired}
            onChange={(next) => set('emailRequired', next)}
            label="They need an email address"
          />
          <div>
            <label htmlFor="email-required" className="block cursor-pointer text-sm font-medium text-slate-800 dark:text-slate-200">
              They need an email address
            </label>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Turn off for roles without a mailbox: some site and contractor roles do not have one.
            </p>
          </div>
        </div>

        {draft.employee.emailRequired && (
          <div className="mt-5 max-w-md">
            <TextField
              label="Preferred email address"
              type="email"
              hint="Leave blank and IT will follow the usual naming convention: surname, dot, first initial."
              value={draft.employee.emailAddress}
              onChange={(value) => set('emailAddress', value)}
              placeholder="Doe.J@nhcopper.com"
              maxLength={160}
            />
          </div>
        )}
      </FieldGroup>
    </div>
  );
}
